import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import AppShell from "../components/AppShell";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Sparkle } from "../components/BrandLogo";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import {
  Heart, Leaf, ShieldCheck, Sparkles, Anchor, Globe, X, Check, BookOpen, ChevronRight,
} from "lucide-react";
import { getPath, dailyPrompt } from "../data/paths";
import { dailyLessonSlug, getLesson, categoryLabel } from "../data/lessons";
import {
  getActivePath, getSuggestedDone, setSuggestedDone,
  getCustomGoal, setCustomGoal,
} from "../lib/localProgress";
import { lightTap } from "../lib/haptics";

const ICONS = {
  anchor: Anchor,
  body: Leaf,
  reflection: Heart,
  safety: ShieldCheck,
  decode: Sparkles,
};

const SPANISH_BANNER_KEY = "ss_spanish_banner_seen";

export default function Today() {
  const { profile } = useAuth();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const lang = i18n.language;

  const [feed, setFeed] = useState({ cards: [] });
  const [loading, setLoading] = useState(true);
  const [showSpanishBanner, setShowSpanishBanner] = useState(false);

  // v1.1 state
  const [pathSlug, setPathSlug] = useState(getActivePath());
  const [suggestedDone, setSugDone] = useState(getSuggestedDone());
  const [custom, setCustom] = useState(getCustomGoal());
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/today");
        setFeed(data);
      } finally { setLoading(false); }
    })();
  }, []);

  useEffect(() => {
    if (!profile) return;
    if (lang !== "en") return;
    try { if (window.localStorage.getItem(SPANISH_BANNER_KEY)) return; } catch { return; }
    setShowSpanishBanner(true);
  }, [profile, lang]);

  const dismissBanner = () => {
    setShowSpanishBanner(false);
    try { window.localStorage.setItem(SPANISH_BANNER_KEY, "1"); } catch { /* ignore */ }
  };

  // suggested daily goal
  const suggestedText = pathSlug ? dailyPrompt(pathSlug, lang) : null;
  const path = pathSlug ? getPath(pathSlug) : null;
  const toggleSuggestedDone = () => {
    const next = !suggestedDone;
    setSuggestedDone(next);
    setSugDone(next);
    if (next) lightTap();
  };

  // custom goal
  const startEditing = () => { setDraft(custom.text || ""); setEditing(true); };
  const saveCustom = () => {
    const next = { text: draft.trim(), done: false };
    setCustomGoal(next);
    setCustom(next);
    setEditing(false);
    lightTap();
  };
  const toggleCustomDone = () => {
    const next = { ...custom, done: !custom.done };
    setCustomGoal(next);
    setCustom(next);
    if (next.done) lightTap();
  };

  // daily lesson
  const lessonSlug = dailyLessonSlug();
  const lesson = getLesson(lessonSlug);

  const greetingName = profile?.display_name
    ? t("today.greeting_with_name", { name: profile.display_name })
    : `${t("today.greeting")}.`;
  const todayLabel = new Date().toLocaleDateString(lang, {
    weekday: "long", month: "long", day: "numeric",
  });

  return (
    <AppShell>
      <div className="px-8 pt-16 pb-6" data-testid="page-today">
        <p className="text-xs uppercase tracking-[0.25em] text-foreground/55">{todayLabel}</p>
        <h1 className="mt-2 font-serif text-4xl leading-tight">{greetingName}</h1>
        <p className="mt-3 text-foreground/70 leading-relaxed">{t("today.intro")}</p>
      </div>

      {showSpanishBanner && (
        <div className="mx-7 mb-4 rounded-3xl bg-brand/8 border border-brand/20 p-4 flex items-start gap-3" data-testid="spanish-banner">
          <Globe className="w-4 h-4 text-brand shrink-0 mt-0.5" />
          <p className="text-sm text-foreground/80 leading-relaxed flex-1">{t("today.spanish_banner")}</p>
          <button data-testid="spanish-banner-dismiss" onClick={dismissBanner} className="text-foreground/40 hover:text-brand" aria-label={t("today.spanish_banner_dismiss")}>
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Understand the patterns — Abuse Library discoverability */}
      <div className="px-8 mb-3" data-testid="today-understand-card">
        <button
          data-testid="today-understand-cta"
          onClick={() => navigate("/library")}
          className="w-full text-left rounded-3xl bg-brand/8 border border-brand/20 p-5 hover:border-brand/50 hover:bg-brand/12 transition-all"
        >
          <div className="flex items-start gap-3">
            <span className="grid place-items-center w-10 h-10 rounded-full bg-brand/15 text-brand shrink-0">
              <BookOpen className="w-5 h-5" />
            </span>
            <div className="flex-1 min-w-0">
              <p className="text-xs uppercase tracking-widest text-brand">{t("today.understand_kicker")}</p>
              <div className="mt-1 font-serif text-xl leading-snug">{t("today.understand_title")}</div>
              <p className="mt-1 text-sm text-foreground/70 leading-relaxed">{t("today.understand_body")}</p>
              <span className="mt-3 inline-flex items-center gap-1 text-xs text-brand">
                {t("today.understand_cta")} <ChevronRight className="w-3 h-3" />
              </span>
            </div>
          </div>
        </button>
      </div>

      {/* Path + suggested goal card */}
      <div className="px-8 mb-3" data-testid="path-card">
        {!pathSlug ? (
          <button
            data-testid="path-empty-cta"
            onClick={() => navigate("/path")}
            className="w-full text-left rounded-3xl bg-card border border-dashed border-brand/30 p-5 hover:border-brand/60 transition-all"
          >
            <p className="text-xs uppercase tracking-widest text-brand">{t("v11.today.path_kicker")}</p>
            <div className="mt-1 font-serif text-xl">{t("v11.today.no_path_title")}</div>
            <p className="text-sm text-foreground/65 mt-1 leading-relaxed">{t("v11.today.no_path_desc")}</p>
            <span className="mt-3 inline-block text-xs text-brand">{t("v11.today.no_path_cta")} →</span>
          </button>
        ) : (
          <div className="rounded-3xl bg-card border border-border p-5">
            <div className="flex items-center justify-between">
              <p className="text-xs uppercase tracking-widest text-brand">{t("v11.today.path_kicker")}</p>
              <button
                data-testid="path-change-btn"
                onClick={() => navigate("/path")}
                className="text-xs text-foreground/55 hover:text-brand"
              >
                {t("v11.today.change_path")}
              </button>
            </div>
            <div className="mt-1 font-serif text-xl" data-testid="path-current-label">
              {path ? (lang === "es" ? path.label_es : path.label_en) : ""}
            </div>
            {suggestedText && (
              <>
                <p className="mt-4 text-xs uppercase tracking-widest text-foreground/55">{t("v11.today.goal_suggested_title")}</p>
                <p className="mt-1 font-serif italic text-foreground/90 leading-snug" data-testid="goal-suggested-text">{suggestedText}</p>
                <button
                  data-testid="goal-suggested-toggle"
                  onClick={toggleSuggestedDone}
                  className={`mt-3 inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm transition-all ${
                    suggestedDone
                      ? "bg-brand/15 text-brand"
                      : "bg-brand text-white hover:bg-brand/90"
                  }`}
                >
                  <Check className="w-3.5 h-3.5" />
                  {suggestedDone ? t("v11.today.goal_suggested_done") : t("v11.today.goal_suggested_mark")}
                </button>
              </>
            )}
          </div>
        )}
      </div>

      {/* Custom personal goal */}
      <div className="px-8 mb-3" data-testid="custom-goal-card">
        <div className="rounded-3xl bg-card border border-border p-5">
          <p className="text-xs uppercase tracking-widest text-foreground/55">{t("v11.today.goal_custom_title")}</p>
          {!editing && custom.text ? (
            <>
              <p className={`mt-1 font-serif italic leading-snug ${
                custom.done ? "text-foreground/55 line-through" : "text-foreground/90"
              }`} data-testid="custom-goal-text">{custom.text}</p>
              <div className="mt-3 flex gap-2">
                <button
                  data-testid="custom-goal-toggle"
                  onClick={toggleCustomDone}
                  className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm transition-all ${
                    custom.done
                      ? "bg-brand/15 text-brand"
                      : "bg-brand text-white hover:bg-brand/90"
                  }`}
                >
                  <Check className="w-3.5 h-3.5" />
                  {custom.done ? t("v11.today.goal_custom_done") : t("v11.today.goal_custom_mark")}
                </button>
                <button
                  data-testid="custom-goal-edit"
                  onClick={startEditing}
                  className="text-xs text-foreground/55 hover:text-brand px-2"
                >
                  ✎
                </button>
              </div>
            </>
          ) : (
            <>
              <Textarea
                data-testid="custom-goal-input"
                value={editing ? draft : ""}
                onChange={(e) => { setEditing(true); setDraft(e.target.value); }}
                placeholder={t("v11.today.goal_custom_placeholder")}
                className="mt-2 min-h-20 rounded-2xl bg-background/60"
              />
              <Button
                data-testid="custom-goal-save"
                onClick={saveCustom}
                disabled={!draft.trim()}
                className="mt-2 rounded-full bg-brand hover:bg-brand/90 text-white h-10"
              >
                {t("v11.today.goal_custom_save")}
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Today's lesson */}
      {lesson && (
        <div className="px-8 mb-3" data-testid="today-lesson-card">
          <button
            onClick={() => navigate(`/lessons/${lesson.slug}`)}
            className="w-full text-left rounded-3xl bg-card border border-border p-5 hover:border-brand/50 transition-all"
          >
            <p className="text-xs uppercase tracking-widest text-brand">{t("v11.today.lesson_card_kicker")}</p>
            <div className="mt-1 font-serif text-xl">{lang === "es" ? lesson.title_es : lesson.title_en}</div>
            <div className="mt-1 flex items-center gap-3 text-xs text-foreground/55">
              <span>{categoryLabel(lesson.category, lang)}</span>
              <span>· {t("v11.lessons.minutes", { n: lesson.read_minutes })}</span>
            </div>
          </button>
          <button
            data-testid="open-lesson-library"
            onClick={() => navigate("/lessons")}
            className="mt-2 inline-flex items-center gap-1 text-xs text-foreground/55 hover:text-brand"
          >
            <BookOpen className="w-3 h-3" /> {t("v11.today.lesson_card_cta")} <ChevronRight className="w-3 h-3" />
          </button>
        </div>
      )}

      {/* Existing server-driven feed */}
      <div className="px-8 space-y-4 pb-10 mt-2">
        {loading && (
          <div className="rounded-3xl bg-card border border-border p-6 animate-soft-pulse h-32" />
        )}
        {!loading && Array.isArray(feed.cards) && feed.cards.map((c, i) => {
          const Icon = ICONS[c.kind] || Sparkles;
          return (
            <article
              key={i}
              data-testid={`today-card-${i}`}
              className="relative rounded-3xl bg-card border border-border p-6 shadow-sm animate-fade-up overflow-hidden"
              style={{ animationDelay: `${i * 80}ms` }}
            >
              <div className="absolute -top-4 -right-4 w-20 h-20 rounded-full bg-brand/5" />
              <div className="flex items-start gap-4 relative">
                <span className="grid place-items-center w-10 h-10 rounded-full bg-brand/10 text-brand shrink-0">
                  <Icon className="w-5 h-5" />
                </span>
                <div>
                  <h3 className="font-serif text-xl leading-snug">{c.title}</h3>
                  <p className="mt-2 text-foreground/75 leading-relaxed text-[15px]">{c.body}</p>
                </div>
              </div>
            </article>
          );
        })}

        <div className="relative rounded-3xl bg-brand/8 border border-brand/20 p-6 mt-2 overflow-hidden">
          <Sparkle className="absolute top-4 right-4 w-3 h-3 text-brand" />
          <p className="font-script text-2xl text-brand">{t("today.note_to_self_kicker")}</p>
          <p className="mt-1 font-serif italic text-foreground/85 text-lg leading-snug">
            {t("today.note_to_self")}
          </p>
        </div>
      </div>
    </AppShell>
  );
}
